import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, CalendarDays, ArrowRight, Receipt, CreditCard, Banknote, Wallet } from "lucide-react";

const PAYMENT_LABELS = { ONLINE: "Stripe Checkout", EPLAYMENT: "ePlayment", CASH: "Pay at Venue", WALLET: "Wallet", VENUE_DEPOSIT: "Venue Deposit", PENDING_CASH: "Pay at Venue" };

export default function BookingReceipt() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const venue = searchParams.get("venue") || "Venue";
  const space = searchParams.get("space") || "";
  const total = Number(searchParams.get("total") || 0);
  const count = Number(searchParams.get("count") || 1);
  const date = searchParams.get("date") || "";
  const ids = (searchParams.get("ids") || "").split(",").filter(Boolean);
  const paymentMode = searchParams.get("pm") || "";
  const txnId = searchParams.get("txn") || "";
  const hasBookingId = ids.length > 0 && ids[0].startsWith("book_");

  const PayIcon = paymentMode === "ONLINE" ? CreditCard : paymentMode === "WALLET" ? Wallet : Banknote;

  return (
    <div className="max-w-md mx-auto px-4 py-10 text-center">
      {/* Success Animation */}
      <div className="mb-6">
        <div className="w-20 h-20 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-4 animate-in zoom-in-50 duration-500">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight" data-testid="receipt-title">Booking Confirmed!</h1>
        <p className="text-muted-foreground mt-1">Your payment was successful</p>
      </div>

      {/* Receipt Card */}
      <div className="border-2 border-slate-200 rounded-sm p-5 text-left mb-6" data-testid="receipt-card">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground">Receipt</h2>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Venue</span>
            <span className="font-medium">{venue}</span>
          </div>
          {space && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Space</span>
              <span className="font-medium">{space}</span>
            </div>
          )}
          {date && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Date</span>
              <span className="font-medium">{date}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Slots Booked</span>
            <span className="font-medium">{count} slot{count > 1 ? "s" : ""}</span>
          </div>
          {ids.length > 0 && ids[0].startsWith("book_") && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Booking ID{ids.length > 1 ? "s" : ""}</span>
              <span className="font-mono text-xs">{ids.length <= 2 ? ids.join(", ") : `${ids[0]}, +${ids.length - 1} more`}</span>
            </div>
          )}
        </div>

        <Separator className="my-3" />

        {/* Payment Info */}
        {paymentMode && (
          <div className="space-y-2 text-sm mb-3" data-testid="receipt-payment-info">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Payment Mode</span>
              <span className="font-medium flex items-center gap-1.5">
                <PayIcon className="w-3.5 h-3.5" />
                {PAYMENT_LABELS[paymentMode] || paymentMode}
              </span>
            </div>
            {txnId && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Transaction ID</span>
                <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded" data-testid="receipt-txn-id">
                  {txnId.length > 20 ? `${txnId.slice(0, 20)}...` : txnId}
                </span>
              </div>
            )}
            <Separator className="my-2" />
          </div>
        )}

        <div className="flex justify-between font-heading text-xl font-bold">
          <span>Total Paid</span>
          <span className="text-green-600" data-testid="receipt-total">{"\u20B1"}{total.toLocaleString()}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        {hasBookingId && (
          <Button data-testid="view-full-receipt-btn" onClick={() => navigate(`/receipts/booking/${ids[0]}`)}
            className="w-full bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide h-12">
            <Receipt className="w-4 h-4 mr-2" />View Full Receipt
          </Button>
        )}
        <Button data-testid="go-to-bookings-btn" onClick={() => navigate("/bookings")}
          className={`w-full ${hasBookingId ? 'bg-primary/90' : 'bg-primary'} hover:bg-primary/90 text-white rounded-full font-heading uppercase tracking-wide h-12`}>
          <CalendarDays className="w-4 h-4 mr-2" />Go to My Bookings
        </Button>
        <Button variant="outline" onClick={() => navigate("/venues")} className="w-full rounded-full border-2">
          Book Another Slot<ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
